import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import TabBar from '../../components/TabBar';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import { ArrowLeft, Bell, Send, Search, User as UserIcon, Users, CheckCircle2, Copy, ChevronDown } from 'lucide-react';
import api from '../../utils/api';
import { formatBJTime } from '../../utils/datetime';
import { copyToClipboard } from '../../utils/clipboard';

// 通知系统：结算通知 / 系统通知推送（管理员 + 获授权代理商）[需求#7：原"结算派发"功能改名]
//  - 结算通知：选单个接收人（注册用户/外部档案）发送
//  - 系统通知：默认全体推送（不选接收人）——管理员=全体；代理商=自己名下
export default function PushMessagePage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { showToast } = useToast();

  const [targets, setTargets] = useState([]);       // 可用接收人
  const [keyword, setKeyword] = useState('');       // 搜索关键字
  const [selectedId, setSelectedId] = useState(''); // 当前选中的 userId
  const [titleType, setTitleType] = useState('settlement'); // 标题二选一：settlement=结算通知 / system=系统通知
  const [systemTarget, setSystemTarget] = useState('all'); // 系统通知发送范围：all=全体 / single=单独通知
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadOK, setLoadOK] = useState(false);
  // 接收人下拉
  const [showTargetDropdown, setShowTargetDropdown] = useState(false);
  const targetTriggerRef = useRef(null);
  const targetListRef = useRef(null);
  const targetDropdownRef = useRef(null);

  // 点击外部关闭接收人下拉
  useEffect(() => {
    if (!showTargetDropdown) return;
    const onDocClick = (e) => {
      const inTrigger = targetDropdownRef.current && targetDropdownRef.current.contains(e.target);
      const inList = targetListRef.current && targetListRef.current.contains(e.target);
      if (!inTrigger && !inList) setShowTargetDropdown(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showTargetDropdown]);

  const loadTargets = useCallback(() => {
    return api.get('/admin/push/targets').then(res => setTargets(res.data.targets || []));
  }, []);

  useEffect(() => {
    Promise.all([
      loadTargets(),
      api.get('/admin/push/history').then(res => setHistory(res.data.history || [])).catch(() => {})
    ]).catch(() => {
      showToast('无通知系统权限或加载失败', 'error');
    }).finally(() => setLoadOK(true));
  }, [loadTargets, showToast]);

  const filtered = targets.filter(t => {
    if (!keyword.trim()) return true;
    const k = keyword.trim().toLowerCase();
    return String(t.name || '').toLowerCase().includes(k)
      || String(t.phone || '').includes(k)
      || String(t.username || '').includes(k);
  });

  const selectedTarget = targets.find(t => String(t.id) === String(selectedId));

  // 选择"系统通知 + 全体"时：全体推送，不需要选择接收人
  const isSystemAll = titleType === 'system' && systemTarget === 'all';

  const copyText = async (text, label = '已复制') => {
    await copyToClipboard(text);
    showToast(label, 'success');
  };

  const handleSend = async () => {
    if (!content.trim()) return showToast('请填写推送内容', 'error');
    if (content.trim().length > 500) return showToast('内容不超过500字', 'error');
    if (!isSystemAll && !selectedTarget) return showToast('请选择接收人', 'error');
    setSending(true);
    try {
      const res = await api.post('/admin/push/send', {
        // 系统通知 → 全体推送（不选目标）；结算通知 → 单条
        ...(isSystemAll ? { all: true } : { userId: selectedTarget.id }),
        titleType,
        content: content.trim(),
      });
      showToast(res.data.message || '已发送', 'success');
      setContent('');
      const h = await api.get('/admin/push/history').catch(() => null);
      if (h) setHistory(h.data.history || []);
    } catch (err) {
      showToast(err.response?.data?.error || '发送失败', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Bell size={16} /> 通知系统
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* ── 选择接收人 ── */}
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
            接收人
          </div>

          {/* 系统通知：先选发送范围（全体 / 单独通知） */}
          {titleType === 'system' && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[
                { value: 'all',    label: '全体推送' },
                { value: 'single', label: '单独通知' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSystemTarget(opt.value)}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', border: '1.5px solid',
                    borderColor: systemTarget === opt.value ? 'var(--c-primary)' : 'var(--c-border)',
                    background:  systemTarget === opt.value ? 'var(--c-primary-light)' : 'transparent',
                    color:       systemTarget === opt.value ? 'var(--c-primary)' : 'var(--c-text-secondary)',
                  }}
                >
                  {opt.value === 'all' && systemTarget === 'all' && <Users size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />}
                  {opt.value === 'single' && systemTarget === 'single' && <UserIcon size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />}
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* 全体推送说明 */}
          {isSystemAll ? (
            <div style={{
              background: 'var(--c-primary-light)', borderRadius: 10, padding: '12px 14px',
              fontSize: 13, color: 'var(--c-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6
            }}>
              <Users size={15} />
              全体推送（{isAdmin ? '管理员·全部用户' : '代理商·我名下用户'}），无需选择接收人
            </div>
          ) : (
            /* 单独通知（结算通知 或 系统通知+单独）：搜索+自定义下拉 */
            <div ref={targetDropdownRef} style={{ position: 'relative' }}>
              {/* 触发按钮：显示已选 or 占位 */}
              <button ref={targetTriggerRef} type="button"
                onClick={() => setShowTargetDropdown(v => !v)}
                style={{
                  width: '100%', height: 40, padding: '0 12px',
                  background: 'var(--c-bg)', border: '1px solid var(--c-border)', borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', fontSize: 14, color: 'var(--c-text)'
                }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8, color: selectedTarget ? 'var(--c-text)' : 'var(--c-text-secondary)' }}>
                  {selectedTarget
                    ? `${selectedTarget.is_external === 1 ? '[外部] ' : ''}${selectedTarget.name || selectedTarget.phone || selectedTarget.username || '未命名'}`
                    : '请选择接收人...'}
                </span>
                <ChevronDown size={16} color="var(--c-text-secondary)" style={{ flexShrink: 0 }} />
              </button>
              {/* 下拉列表（不用 portal，直接 absolute，push-down 布局） */}
              {showTargetDropdown && (
                <div ref={targetListRef}
                  style={{
                    position: 'absolute', zIndex: 9999, marginTop: 4,
                    width: targetTriggerRef.current ? targetTriggerRef.current.offsetWidth : '100%',
                    background: 'var(--c-surface)', border: '1px solid var(--c-border)',
                    borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden'
                  }}>
                  {/* 搜索框 */}
                  <div style={{ padding: 8, borderBottom: '1px solid var(--c-divider)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Search size={14} color="var(--c-text-secondary)" />
                      <input className="input" style={{ flex: 1, height: 34 }}
                        placeholder="搜索昵称 / 手机号 / 账号"
                        value={keyword} onChange={e => setKeyword(e.target.value)}
                      />
                    </div>
                  </div>
                  {/* 选项列表 */}
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                      <div style={{ padding: '14px 12px', fontSize: 13, color: 'var(--c-text-secondary)', textAlign: 'center' }}>
                        暂无匹配的接收人
                      </div>
                    ) : filtered.map(t => (
                      <div key={t.id}
                        onClick={() => { setSelectedId(String(t.id)); setShowTargetDropdown(false); setKeyword(''); }}
                        style={{
                          padding: '10px 12px', cursor: 'pointer',
                          background: String(t.id) === selectedId ? 'var(--c-primary-light)' : 'transparent',
                          borderBottom: '1px solid var(--c-divider)'
                        }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          {t.is_external === 1 ? <span style={{ fontSize: 11, color: 'var(--c-accent)', marginRight: 4 }}>[外部]</span> : null}
                          {t.name || t.username || (t.phone && !String(t.phone).startsWith('ext') ? t.phone : '') || '未命名'}
                          {t.phone && !String(t.phone).startsWith('ext') && t.name !== t.phone && (
                            <span style={{ fontWeight: 400, color: 'var(--c-text-secondary)', marginLeft: 4 }}>（{t.phone}）</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── 标题（二选一）── */}
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
            推送标题（用户收到的弹窗标题）
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { value: 'settlement', label: '结算通知' },
              { value: 'system', label: '系统通知' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTitleType(opt.value)}
                className={`btn ${titleType === opt.value ? 'btn-primary' : 'btn-outline'}`}
                style={{ flex: 1, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                {titleType === opt.value && <CheckCircle2 size={15} />}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 内容 ── */}
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
            推送内容（用户点击后以系统弹窗展示）
          </div>
          <textarea
            className="input"
            rows={4}
            placeholder="例如：本周完成 300 条，工资 ¥210 已结算，请查收。"
            value={content}
            maxLength={500}
            onChange={e => setContent(e.target.value)}
            style={{ resize: 'none', lineHeight: 1.7 }}
          />
          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4, textAlign: 'right' }}>
            {content.length} / 500
          </div>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending} style={{ width: '100%' }}>
            <Send size={15} />
            {sending ? '发送中...' : '发送'}
          </button>
        </div>

        {/* ── 历史 ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '0.5px solid var(--c-border)', fontSize: 14, fontWeight: 700 }}>
            发送记录
          </div>
          {history.length === 0 ? (
            <p style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-secondary)', fontSize: 13 }}>
              暂无发送记录
            </p>
          ) : (
            <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
              {history.map(n => (
                <div key={n.id} style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--c-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>
                      {n.is_external === 1 ? '[外部] ' : ''}{n.user_name || n.user_phone || `用户${n.user_id}`}
                    </span>
                    <span style={{ color: 'var(--c-text-secondary)', fontSize: 12 }}>
                      {n.created_at ? formatBJTime(n.created_at) : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {n.content}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                    <span className="badge badge-info" style={{ fontSize: 10 }}>{n.title || '系统通知'}</span>
                    <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '3px 10px' }}
                      onClick={() => copyText(n.content, '已复制内容')}>
                      <Copy size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />复制内容
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 首次加载失败（无权限）时给返回提示 */}
      {!loadOK && (
        <div style={{ padding: '0 16px', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>无通知系统权限，请联系管理员开通。</p>
        </div>
      )}

      <TabBar mode="admin" />
    </div>
  );
}